import MarkdownPreview from "@/components/stories/markdown-preview"
import StoryPlatformPicker from "@/components/stories/story-platform-picker"
import { Button } from "@/components/ui/button"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import { useTimeZone } from "@/hooks/use-time-zone"
import { zonedWallClockToIso } from "@/lib/scheduling"
import type { Platform, PlatformCredentials, YoutubeCategory } from "@/types"

import { type StoryFormData, emptyTakeAttributes } from "./story-form-utils"

interface Props {
  data: StoryFormData
  errors: Partial<Record<string, string>>
  processing: boolean
  availablePlatforms?: Platform[]
  platformCredentials?: PlatformCredentials
  youtubeCategories: YoutubeCategory[]
  defaultYoutubeCategoryId: number
  onChange: (value: StoryFormData) => void
  onSubmit: (e: React.FormEvent, value: StoryFormData) => void | Promise<void>
  onPublish?: (e: React.FormEvent, value: StoryFormData) => void | Promise<void>
  onCancel: () => void
}

// A long-form story is a single take whose body is one piece of markdown. The
// take is always keyed at 0, so reads and writes target that entry directly.
const TAKE_KEY = 0

export default function LongFormStoryForm({
  data,
  errors,
  processing,
  availablePlatforms = [],
  platformCredentials,
  youtubeCategories,
  defaultYoutubeCategoryId,
  onChange,
  onSubmit,
  onPublish,
  onCancel,
}: Props) {
  const timeZone = useTimeZone()
  const take = data.takes_attributes[TAKE_KEY] ?? {
    ...emptyTakeAttributes(),
    position: 0,
  }
  const body = take.body

  function updateBody(nextBody: string) {
    onChange({
      ...data,
      takes_attributes: {
        [TAKE_KEY]: { ...take, type: "Take", body: nextBody, position: 0 },
      },
    })
  }

  function submitWith(
    e: React.FormEvent,
    callback: (
      e: React.FormEvent,
      value: StoryFormData,
    ) => void | Promise<void>,
  ) {
    e.preventDefault()
    const publishAtIso = data.scheduled_at
      ? (zonedWallClockToIso(data.scheduled_at, timeZone) ?? undefined)
      : undefined

    void callback(e, {
      title: data.title,
      tags: data.tags,
      scheduled_at: publishAtIso,
      syndications_attributes: data.syndications_attributes,
      takes_attributes: {
        [TAKE_KEY]: { ...take, type: "Take", body, position: 0 },
      },
    })
  }

  const scheduleSet = Boolean(data.scheduled_at)
  const formError = errors.takes ?? errors.base

  return (
    <form
      onSubmit={(e) => submitWith(e, onSubmit)}
      className="flex flex-col gap-6 pb-24 md:pb-0"
    >
      {formError && <p className="text-destructive text-sm">{formError}</p>}

      <div className="flex flex-col gap-2">
        <Label htmlFor="long-form-body">Story</Label>
        <p className="text-muted-foreground text-sm">
          Write the whole story as a single piece of markdown. It publishes as
          one long-form post.
        </p>
        <div className="grid gap-4 md:grid-cols-2">
          <Textarea
            id="long-form-body"
            value={body}
            onChange={(event) => updateBody(event.target.value)}
            placeholder={"# Title\n\nWrite your story in markdown..."}
            className="min-h-[28rem] font-mono text-sm"
          />
          <div className="min-h-[28rem] rounded-md border p-4">
            <MarkdownPreview source={body} />
          </div>
        </div>
      </div>

      <StoryPlatformPicker
        availablePlatforms={availablePlatforms}
        platformCredentials={platformCredentials}
        syndicationsAttributes={data.syndications_attributes}
        publishAt={data.scheduled_at ?? ""}
        onSyndicationsAttributesChange={(next) =>
          onChange({ ...data, syndications_attributes: next })
        }
        onPublishAtChange={(value) =>
          onChange({ ...data, scheduled_at: value })
        }
        platformsError={errors.syndications}
        publishAtError={errors.scheduled_at}
        youtubeCategories={youtubeCategories}
        defaultYoutubeCategoryId={defaultYoutubeCategoryId}
        title={data.title ?? ""}
        tags={data.tags ?? []}
        onTitleChange={(value) => onChange({ ...data, title: value })}
        onTagsChange={(value) => onChange({ ...data, tags: value })}
      />

      <div
        className={[
          "bg-background/95 fixed inset-x-0 bottom-0 z-20 flex gap-3 border-t px-6 py-3 backdrop-blur",
          "pb-[max(env(safe-area-inset-bottom),0.75rem)]",
          "md:static md:inset-x-auto md:bottom-auto md:z-auto md:border-0 md:bg-transparent md:px-0 md:py-0 md:pt-2 md:backdrop-blur-none",
        ].join(" ")}
      >
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          className="flex-1 md:flex-none"
        >
          Cancel
        </Button>
        <Button
          type="submit"
          disabled={processing}
          className="flex-1 md:flex-none"
        >
          Save Draft
        </Button>
        {onPublish &&
          (scheduleSet ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <span tabIndex={0} className="flex-1 md:flex-none">
                  <Button
                    type="button"
                    disabled
                    className="pointer-events-none w-full"
                  >
                    Save and publish
                  </Button>
                </span>
              </TooltipTrigger>
              <TooltipContent>
                A scheduled time is set — save the draft and it&apos;ll publish
                then.
              </TooltipContent>
            </Tooltip>
          ) : (
            <Button
              type="button"
              disabled={processing}
              onClick={(e) => submitWith(e, onPublish)}
              className="flex-1 md:flex-none"
            >
              Save and publish
            </Button>
          ))}
      </div>
    </form>
  )
}
